import React, { useCallback, useEffect, useRef, useState } from 'react';
import SelfieCaptureDialog from '../EventGallery/SelfieCaptureDialog';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Dialog,
  DialogContent, Grid, IconButton, Paper, Stack, Typography,
  useMediaQuery, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CameraAltOutlinedIcon from '@mui/icons-material/CameraAltOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import CollectionsIcon from '@mui/icons-material/Collections';
import PersonIcon from '@mui/icons-material/Person';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import invitationApi from './invitationApi';

const STATUS_LABEL = {
  none: 'Not yet processed',
  processing: 'Processing…',
  uploading: 'Uploading…',
  complete: 'Ready',
  failed: 'Failed',
};
const STATUS_COLOR = { none: 'default', processing: 'warning', uploading: 'warning', complete: 'success', failed: 'error' };

function PhotoGrid({ photoIds, thumbnails, onClickPhoto }) {
  if (!photoIds.length) return null;
  return (
    <Grid container spacing={0.5}>
      {photoIds.map(pid => (
        <Grid size={{ xs: 4, sm: 3, md: 2 }} key={pid}>
          <Box
            component="img"
            src={thumbnails[pid]}
            alt=""
            onClick={() => onClickPhoto(pid)}
            sx={{
              width: '100%',
              aspectRatio: '1',
              objectFit: 'cover',
              cursor: 'pointer',
              borderRadius: 1,
              '&:hover': { opacity: 0.85 },
            }}
          />
        </Grid>
      ))}
    </Grid>
  );
}

function PhotoLightbox({ photoId, previews, onClose }) {
  if (!photoId || !previews[photoId]) return null;
  return (
    <Dialog open maxWidth="lg" onClose={onClose}
      PaperProps={{ sx: { bgcolor: 'black', m: 1, maxHeight: '95vh' } }}>
      <DialogContent sx={{ p: 0, position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <IconButton onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'white', zIndex: 1, bgcolor: 'rgba(0,0,0,0.5)' }}>
          <CloseIcon />
        </IconButton>
        <Box component="img" src={previews[photoId]} alt=""
          sx={{ maxWidth: '100%', maxHeight: '90vh', objectFit: 'contain' }} />
      </DialogContent>
    </Dialog>
  );
}

function SelfieSection({ invitationId, token, eventId, onSubmitted }) {
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreview, setSelfiePreview] = useState('');
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [selfieCamOpen, setSelfieCamOpen] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => () => { if (selfiePreview) URL.revokeObjectURL(selfiePreview); }, [selfiePreview]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (selfiePreview) URL.revokeObjectURL(selfiePreview);
    setSelfieFile(f);
    setSelfiePreview(URL.createObjectURL(f));
    setError('');
  };

  const handleSubmit = async () => {
    if (!selfieFile) { setError('Please select a selfie photo.'); return; }
    if (!consent) { setError('Please consent to face matching to find your photos.'); return; }
    setSubmitting(true);
    setError('');
    try {
      await invitationApi.submitInviteeSelfie(invitationId, token, eventId, selfieFile, true);
      setDone(true);
      onSubmitted();
    } catch (e) {
      setError(e.message || 'Failed to submit selfie');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 36, mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={700}>Selfie submitted!</Typography>
        <Typography variant="body2" color="text.secondary" mt={0.5}>
          Once the organiser processes photos, your matched photos will appear here.
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={700} gutterBottom>
        Add your selfie to find your photos
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Upload a clear photo of your face. We'll use it to find photos of you from the event.
      </Typography>

      <SelfieCaptureDialog
        open={selfieCamOpen}
        onClose={() => setSelfieCamOpen(false)}
        onCapture={file => {
          if (selfiePreview) URL.revokeObjectURL(selfiePreview);
          setSelfieFile(file);
          setSelfiePreview(URL.createObjectURL(file));
          setError('');
          setSelfieCamOpen(false);
        }}
      />

      {selfiePreview ? (
        <Box sx={{ mb: 1.5 }}>
          <Box component="img" src={selfiePreview} alt="Selfie preview"
            sx={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 2, mb: 1, display: 'block' }} />
          <div className="selfie-capture-row" style={{ justifyContent: 'flex-start' }}>
            <Button variant="outlined" size="small" startIcon={<CameraAltOutlinedIcon />}
              onClick={() => setSelfieCamOpen(true)} sx={{ textTransform: 'none' }}>
              Retake
            </Button>
            <label className="selfie-upload-icon-btn" title="Upload from gallery" style={{ cursor: 'pointer' }}>
              <input type="file" accept="image/*" style={{ display: 'none' }} ref={inputRef} onChange={handleFile} />
              <FileUploadOutlinedIcon fontSize="small" />
            </label>
          </div>
        </Box>
      ) : (
        <div className="selfie-capture-area" style={{ marginBottom: 12 }}>
          <div className="selfie-capture-row">
            <Button variant="contained" startIcon={<CameraAltOutlinedIcon />}
              onClick={() => setSelfieCamOpen(true)}
              sx={{ textTransform: 'none', borderRadius: '999px', background: 'linear-gradient(135deg, #c73866 0%, #fd8f52 100%)', boxShadow: '0 4px 14px rgba(199,56,102,0.28)' }}>
              Take Selfie
            </Button>
            <label className="selfie-upload-icon-btn" title="Upload from gallery">
              <input type="file" accept="image/*" style={{ display: 'none' }} ref={inputRef} onChange={handleFile} />
              <FileUploadOutlinedIcon fontSize="small" />
            </label>
          </div>
          <span className="selfie-capture-hint">or tap the upload icon to choose from gallery</span>
        </div>
      )}

      <Stack direction="row" spacing={1} alignItems="flex-start" mb={1.5}>
        <input
          type="checkbox"
          id="inv-consent"
          checked={consent}
          onChange={e => setConsent(e.target.checked)}
          style={{ marginTop: 3, flexShrink: 0 }}
        />
        <Typography component="label" htmlFor="inv-consent" variant="body2" sx={{ cursor: 'pointer' }}>
          I consent to my photo being used for face matching to find photos of me at this event.
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Button
        variant="contained"
        fullWidth
        onClick={handleSubmit}
        disabled={submitting || !selfieFile}
        sx={{ textTransform: 'none', fontWeight: 600 }}
      >
        {submitting ? <CircularProgress size={18} color="inherit" /> : 'Submit selfie'}
      </Button>
    </Paper>
  );
}

export default function InviteEventView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { invitation_id } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxPhoto, setLightboxPhoto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await invitationApi.getEventView(invitation_id, token);
      setData(res);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load event.');
    } finally {
      setLoading(false);
    }
  }, [invitation_id, token]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', py: 4 }}>
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ mb: 2 }}>{error || 'Invitation not found.'}</Alert>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
            Go home
          </Button>
        </Container>
      </Box>
    );
  }

  const { event, organiser_name, grouping_complete, thumbnails, previews, my_photo_ids, my_request } = data;
  const hasSelfie = my_request?.has_selfie;
  const hasMyPhotos = (my_photo_ids || []).length > 0;

  const eventDate = event?.event_date
    ? new Date(event.event_date).toLocaleDateString()
    : null;

  return (
    <Box sx={{ width: '100%', minHeight: '100vh', bgcolor: 'background.default', pb: isMobile ? 8 : 0 }}>
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/invite/view/${invitation_id}${token ? `?token=${token}` : ''}`)}
          sx={{ mb: 2, textTransform: 'none' }}
        >
          Back to invitation
        </Button>

        {/* Event header */}
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Box>
              <Typography variant="h5" fontWeight={700}>{event?.event_name}</Typography>
              {eventDate && (
                <Typography variant="body2" color="text.secondary">{eventDate}</Typography>
              )}
              {event?.event_type && (
                <Chip label={event.event_type} size="small" variant="outlined" sx={{ mt: 0.5 }} />
              )}
              {organiser_name && (
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Organised by <strong>{organiser_name}</strong>
                </Typography>
              )}
            </Box>
            <Chip
              label={STATUS_LABEL[event?.grouping_status] || event?.grouping_status}
              color={STATUS_COLOR[event?.grouping_status] || 'default'}
              size="small"
            />
          </Stack>
        </Paper>

        <Grid container spacing={3}>
          {/* Left: selfie CTA + status */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Stack spacing={2}>
              {!hasSelfie && (
                <SelfieSection
                  invitationId={invitation_id}
                  token={token}
                  eventId={event?.event_id}
                  onSubmitted={load}
                />
              )}

              {hasSelfie && !hasMyPhotos && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <CheckCircleOutlineIcon color="success" sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="subtitle2" fontWeight={700}>Selfie received</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {grouping_complete
                      ? 'No matching photos found yet — the organiser may add more photos.'
                      : 'Your photos will appear here once the organiser processes the event.'}
                  </Typography>
                </Paper>
              )}

              {hasMyPhotos && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, textAlign: 'center' }}>
                  <PersonIcon color="primary" sx={{ fontSize: 32, mb: 0.5 }} />
                  <Typography variant="subtitle2" fontWeight={700}>{my_photo_ids.length} photo{my_photo_ids.length !== 1 ? 's' : ''} shared with you</Typography>
                </Paper>
              )}

              {!grouping_complete && (
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    The organiser hasn't processed photos yet. Check back after the event to find your photos.
                  </Typography>
                </Paper>
              )}
            </Stack>
          </Grid>

          {/* Right: only photos shared with this invitee */}
          <Grid size={{ xs: 12, md: 8 }}>
            {hasMyPhotos ? (
              <PhotoGrid
                photoIds={my_photo_ids}
                thumbnails={thumbnails}
                onClickPhoto={setLightboxPhoto}
              />
            ) : grouping_complete ? (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <CollectionsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography>No photos have been shared with you yet.</Typography>
                {!hasSelfie && (
                  <Typography variant="body2" mt={0.5}>
                    Add your selfie to help the organiser find your photos.
                  </Typography>
                )}
              </Box>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center', color: 'text.secondary' }}>
                <CollectionsIcon sx={{ fontSize: 48, mb: 1, opacity: 0.4 }} />
                <Typography>Photos will appear here once the organiser processes the event.</Typography>
              </Box>
            )}
          </Grid>
        </Grid>
      </Container>

      <PhotoLightbox
        photoId={lightboxPhoto}
        previews={previews || {}}
        onClose={() => setLightboxPhoto(null)}
      />
    </Box>
  );
}
